import { useState } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  Receipt,
  PieChart,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  User,
  MapPin,
  Map
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTrips } from '@/context/TripsContext';
import logoWithText from '../ux/images/logoWithText.png';

interface SidebarProps {
  className?: string;
}

export const Sidebar = ({ className }: SidebarProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const { tripId: urlTripId } = useParams();
  const { currentTrip, getLastTripId } = useTrips();
  const navigate = useNavigate();

  // Use tripId from URL params, or fall back to currentTrip short_id, or last trip ID
  const tripId = urlTripId || currentTrip?.short_id || getLastTripId();

  // Check if we're on the trips selection page
  const isTripsSelectionPage = location.pathname === '/trips';

  const mainItems = [
    {
      name: 'Dashboard',
      icon: LayoutDashboard,
      path: `/trip/${tripId}`,
    },
    {
      name: 'Expenses',
      icon: Receipt,
      path: `/trip/${tripId}/expenses`,
    },
    ...(currentTrip?.use_budget ? [{
      name: 'Budget',
      icon: PieChart,
      path: `/trip/${tripId}/budget`,
    }] : []),
    {
      name: 'Itinerary',
      icon: MapPin,
      path: `/trip/${tripId}/itinerary`,
    },
    {
      name: 'Reports',
      icon: FileText,
      path: `/trip/${tripId}/reports`,
    },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div
      className={cn(
        "relative flex flex-col border-r bg-background transition-all duration-300 h-screen",
        isCollapsed ? "w-16" : "w-full md:w-64",
        className
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-center border-b px-3">
        <img
          src={logoWithText}
          alt="TravelDashboard"
          className={cn(
            "transition-all duration-300 object-contain",
            isCollapsed ? "h-8 w-8" : "h-10 max-w-full"
          )}
        />
      </div>

      {/* Main Navigation - Hide on trips selection page */}
      {!isTripsSelectionPage && (
        <div className="flex-1 overflow-y-auto py-4">
          <div className="px-3 space-y-1">
            <div className="flex items-center justify-between px-3 mb-2">
              {!isCollapsed && (
                <p className="text-xs md:text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Main
                </p>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="h-6 w-6 md:flex hidden"
                title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronLeft className="h-4 w-4" />
                )}
              </Button>
            </div>

            {mainItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={active ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start gap-3 h-12 md:h-10 text-base md:text-sm",
                      isCollapsed && "justify-center px-2"
                    )}
                    title={isCollapsed ? item.name : undefined}
                  >
                    <Icon className="h-6 w-6 md:h-5 md:w-5 flex-shrink-0" />
                    {!isCollapsed && <span>{item.name}</span>}
                  </Button>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Spacer when on trips page to push bottom section down */}
      {isTripsSelectionPage && <div className="flex-1" />}

      {/* Bottom Section - Settings & Profile */}
      <div className="border-t">
        <div className="p-3 space-y-1">
          <Link to="/trips">
            <Button
              variant={isActive('/trips') ? "secondary" : "ghost"}
              className={cn(
                "w-full justify-start gap-3 h-12 md:h-10 text-base md:text-sm",
                isCollapsed && "justify-center px-2"
              )}
              title={isCollapsed ? "My Trips" : undefined}
            >
              <Map className="h-6 w-6 md:h-5 md:w-5 flex-shrink-0" />
              {!isCollapsed && <span>My Trips</span>}
            </Button>
          </Link>

          <Link to={`/trip/${tripId}/settings`}>
            <Button
              variant={isActive(`/trip/${tripId}/settings`) ? "secondary" : "ghost"}
              className={cn(
                "w-full justify-start gap-3 h-12 md:h-10 text-base md:text-sm",
                isCollapsed && "justify-center px-2"
              )}
              title={isCollapsed ? "Settings" : undefined}
            >
              <Settings className="h-6 w-6 md:h-5 md:w-5 flex-shrink-0" />
              {!isCollapsed && <span>Settings</span>}
            </Button>
          </Link>

          <Button
            variant={isActive('/profile') ? "secondary" : "ghost"}
            onClick={() => navigate('/profile')}
            className={cn(
              "w-full justify-start gap-3 h-12 md:h-10 text-base md:text-sm",
              isCollapsed && "justify-center px-2"
            )}
            title={isCollapsed ? "Profile" : undefined}
          >
            <User className="h-6 w-6 md:h-5 md:w-5 flex-shrink-0" />
            {!isCollapsed && <span>Profile</span>}
          </Button>
        </div>
      </div>
    </div>
  );
};
